import apiClient from './apiClient';
import { User } from './userService';
import { Session } from './sessionService';
import { getAllSessions } from './sessionService';
import { getUsers } from './userService';
import { getAllProblems } from './problemService';
import { getEnrollmentsBySession } from './enrollmentService';

export interface DashboardStats {
  totalStudents: number;
  activeSessions: number;
  totalProblems: number;
  totalSubmissions: number;
}

export interface SessionWithEnrollment extends Session {
  students: number;
}

export interface ActivityItem {
  id: string;
  type: 'signup' | 'payment' | 'submission' | 'achievement';
  title: string;
  description: string;
  timestamp: string; // ISO timestamp
}

// Get the recent activity feed for the admin dashboard
export const getRecentActivity = async (limit = 10): Promise<ActivityItem[]> => {
  const response = await apiClient.get<ActivityItem[]>('/api/activity', { params: { limit } });
  return response.data;
};

// Get dashboard stats
export const getDashboardStats = async (): Promise<DashboardStats> => {
  try {
    // Fetch all stats in parallel for better performance
    const [userResponse, sessionsResponse, problemsResponse] = await Promise.all([
      getUsers({ role: 'student' }).catch(err => {
        console.error('Failed to fetch student users:', err);
        return { total: 0, users: [], page: 1, limit: 10 };
      }),
      getAllSessions().catch(err => {
        console.error('Failed to fetch sessions:', err);
        return [];
      }),
      getAllProblems().catch(err => {
        console.error('Failed to fetch problems:', err);
        return [];
      })
    ]);

    // Calculate active sessions from sessions response
    const now = new Date();
    const activeSessions = sessionsResponse.filter(session => {
      const startDate = new Date(session.startDate);
      const endDate = new Date(session.endDate);
      return startDate <= now && endDate >= now;
    }).length;

    // For submissions, we need to make another API call to get the count
    let totalSubmissions = 0;
    try {
      const submissionsResponse = await apiClient.get('/api/submissions/count');
      totalSubmissions = submissionsResponse.data.count || 0;
    } catch (error) {
      console.warn('Submissions count endpoint error:', error);
      // Check if this is an auth error - Axios errors have a response property
      const axiosError = error as { response?: { status: number } };
      if (axiosError.response?.status === 401) {
        console.warn('Authentication error when fetching submission count - user may need to log in again');
      }

      // Fallback: estimate based on students × problems × 0.7 completion rate
      totalSubmissions = Math.round(userResponse.total * problemsResponse.length * 0.7);
    }

    const stats = {
      totalStudents: userResponse.total,
      activeSessions,
      totalProblems: problemsResponse.length,
      totalSubmissions
    };

    return stats;
  } catch (error) {
    console.error('Error fetching dashboard stats:', error);
    // Return default values if API calls fail
    return {
      totalStudents: 0,
      activeSessions: 0,
      totalProblems: 0,
      totalSubmissions: 0
    };
  }
};

// Get recent sessions with enrollment counts
export const getRecentSessionsWithEnrollment = async (limit = 3): Promise<SessionWithEnrollment[]> => {
  try {
    // Get all sessions
    const sessions = await getAllSessions();

    // Sort by latest first
    const sortedSessions = [...sessions].sort((a, b) =>
      new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
    );

    // Get the most recent sessions
    const recentSessions = sortedSessions.slice(0, limit);

    // For each session, get enrollment count
    const sessionsWithEnrollment = await Promise.all(
      recentSessions.map(async (session) => {
        try {
          const enrollmentResponse = await getEnrollmentsBySession(session.id, 1, 1);
          return {
            ...session,
            students: enrollmentResponse.pagination.total
          };
        } catch (error) {
          console.error(`Error fetching enrollment for session ${session.id}:`, error);
          return {
            ...session,
            students: 0
          };
        }
      })
    );

    return sessionsWithEnrollment;
  } catch (error) {
    console.error('Error fetching recent sessions with enrollment:', error);
    return [];
  }
};

// Get recently joined users
export const getRecentUsers = async (limit = 5): Promise<User[]> => {
  try {
    const response = await getUsers({
      limit,
      page: 1,
      // Sort is handled on the backend by created_at DESC
    });

    return response.users;
  } catch (error) {
    console.error('Error fetching recent users:', error);
    return [];
  }
};

// Get student signups for the last 30 days
export interface StudentSignupData {
  date: string;
  count: number;
}

export const getStudentSignupsLast30Days = async (): Promise<StudentSignupData[]> => {
  try {
    // Fetch users (students)
    // We'll fetch a larger limit to get a good sample, or ideally we'd have a specific endpoint
    const response = await getUsers({
      role: 'student',
      limit: 1000, // Fetch enough to get recent history
      page: 1
    });

    const users = response.users;
    const last30Days = new Array(30).fill(0).map((_, i) => {
      const d = new Date();
      d.setDate(d.getDate() - i);
      return d.toISOString().split('T')[0];
    }).reverse();

    const signupsByDate = users.reduce((acc, user) => {
      try {
        if (!user.createdAt) return acc;
        const date = new Date(user.createdAt).toISOString().split('T')[0];
        acc[date] = (acc[date] || 0) + 1;
      } catch (e) {
        // Skip users with invalid dates
      }
      return acc;
    }, {} as Record<string, number>);

    return last30Days.map(date => ({
      date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      count: signupsByDate[date] || 0
    }));
  } catch (error) {
    console.error('Error fetching student signups:', error);
    // Return empty data structure for last 30 days
    return new Array(30).fill(0).map((_, i) => {
      const d = new Date();
      d.setDate(d.getDate() - (29 - i));
      return {
        date: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        count: 0
      };
    });
  }
};
